import { useState, useEffect, useMemo, useRef } from 'react'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { MapPin, Globe, Search, Loader2, Info } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useTranslation } from 'react-i18next'
import { CountryOption, getCountryOptions } from '@/data/countries'
import {
    LocationSearchError,
    MIN_CITY_QUERY_LEN,
    searchLocations,
    type LocationSearchErrorKind,
} from '@/services'

// NOTE: `toHalfStepDecimal` used to live here, rounding a fetched offset onto a
// quarter-hour ladder. It was removed with the timeapi.io lookup it fed: rounding
// destroys genuinely exact historical offsets (Calcutta pre-1906 was UTC+05:21:10),
// and the lookup itself answered "what is the offset there NOW", which is the wrong
// question for a birth chart. The offset in force at the birth instant is resolved
// by the owning form via `resolveBirthTimezone`, using the IANA zone captured below.

const tzOptions = Array.from({ length: (14 - (-12)) * 4 + 1 }, (_, i) => {
    const val = (-12) + i * 0.25
    return {
        value: val.toString(),
        label: `UTC ${val >= 0 ? '+' : ''}${val}`
    }
})

const normalizeCountryName = (value: string | undefined | null) =>
    String(value || '').trim().toLowerCase()

const GEOAPIFY_URL = 'https://www.geoapify.com/'
const OSM_COPYRIGHT_URL = 'https://www.openstreetmap.org/copyright'

interface LocationData {
    locationName: string
    latitude: string
    longitude: string
    timezoneOffset: string
    /** IANA zone for the selected place, e.g. "America/New_York". Set from the
     *  autocomplete result. Optional: typed-in places and older saved forms have
     *  none, and callers then fall back to resolving from latitude/longitude. */
    timezoneName?: string
    timezoneSource?: '' | 'auto' | 'manual'
    timezoneFold?: number | null
    timezoneResolverVersion?: number | null
    country?: string
    countryName?: string
}

interface LocationInputProps {
    data: LocationData
    onChange: (data: LocationData) => void
    label?: string
    showInlineHelp?: boolean
    /**
     * Replaces the manual UTC-offset dropdown.
     *
     * A birth chart's offset depends on the birth DATE, which this component does
     * not know — only the owning form does. Forms that resolve the offset pass
     * their own display here; callers that genuinely want a hand-picked offset
     * (Global Risk, which asks about a place today, not a birth) pass nothing and
     * keep the dropdown.
     */
    timezoneSlot?: React.ReactNode
}

export function LocationInput({ data, onChange, label, showInlineHelp = false, timezoneSlot }: LocationInputProps) {
    const { toast } = useToast()
    const { t, i18n } = useTranslation('chat')
    const [cityQuery, setCityQuery] = useState(data.locationName)
    const [cityItems, setCityItems] = useState<any[]>([])
    const [showCityDropdown, setShowCityDropdown] = useState(false)
    const [autoFill, setAutoFill] = useState(true)
    const [loadingCity, setLoadingCity] = useState(false)
    const [noCityResults, setNoCityResults] = useState(false)
    const [autoResolving, setAutoResolving] = useState(false)
    // Provider failure, kept distinct from `noCityResults`: an unknown village
    // and a dead backend need different messages, and only one deserves Retry.
    const [searchError, setSearchError] = useState<LocationSearchErrorKind | null>(null)

    const debounceRef = useRef<NodeJS.Timeout | null>(null)
    const searchAbortRef = useRef<AbortController | null>(null)
    const searchSeqRef = useRef(0)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const lastResolvedQueryRef = useRef('')
    const resolvingRef = useRef(false)
    const latestDataRef = useRef(data)
    const locationHelpText = t('chat.location_input.help_text')
    const countries = useMemo(
        () => getCountryOptions(i18n.resolvedLanguage || i18n.language || 'en'),
        [i18n.language, i18n.resolvedLanguage],
    )

    const cancelPendingCitySearch = () => {
        searchSeqRef.current += 1
        if (debounceRef.current) clearTimeout(debounceRef.current)
        debounceRef.current = null
        searchAbortRef.current?.abort()
        searchAbortRef.current = null
        setLoadingCity(false)
    }

    const invalidateAutomaticTimezone = (
        next: LocationData,
        nextTimezoneName = '',
    ): LocationData => {
        const updated: LocationData = { ...next, timezoneName: nextTimezoneName }
        if (timezoneSlot && data.timezoneSource !== 'manual') {
            updated.timezoneOffset = ''
            updated.timezoneSource = ''
            updated.timezoneFold = null
            updated.timezoneResolverVersion = null
        }
        return updated
    }

    const findCountryMatch = (countryName?: string, countryCode?: string): CountryOption | null => {
        const code = String(countryCode || '').trim().toUpperCase()
        if (code) {
            const codeMatch = countries.find(c => c.id.toUpperCase() === code)
            if (codeMatch) return codeMatch
        }

        const normalizedName = normalizeCountryName(countryName)
        if (!normalizedName) return null

        return countries.find(c =>
            normalizeCountryName(c.text) === normalizedName
            || normalizeCountryName(c.englishName) === normalizedName
        )
            || countries.find(c =>
                normalizeCountryName(c.text).includes(normalizedName)
                || normalizeCountryName(c.englishName).includes(normalizedName)
            )
            || countries.find(c =>
                normalizedName.includes(normalizeCountryName(c.text))
                || normalizedName.includes(normalizeCountryName(c.englishName))
            )
            || null
    }

    // Sync local state if parent updates data externally (e.g. load from localstorage)
    useEffect(() => {
        latestDataRef.current = data
        if (data.locationName !== cityQuery) {
            setCityQuery(data.locationName)
        }
    }, [data, data.locationName])

    // Backfill older saved locations that have a country name but no ISO code.
    useEffect(() => {
        if (!countries.length || data.country || !data.countryName) return
        const match = findCountryMatch(data.countryName)
        if (match) {
            const nextData = { ...data, country: match.id, countryName: match.englishName }
            latestDataRef.current = nextData
            onChange(nextData)
        }
    }, [countries, data.country, data.countryName])

    // Outside click
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowCityDropdown(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
            searchAbortRef.current?.abort()
        }
    }, [])

    const handleCitySearch = (query: string) => {
        // Cancel immediately, before React renders the new value. Otherwise an
        // older response can land in that gap and replace the new query's list.
        cancelPendingCitySearch()
        const requestId = searchSeqRef.current
        setCityQuery(query)
        // Update parent immediately as text. Editing after a pick also
        // invalidates the picked coordinates — clear them so stale lat/lon
        // can't ride under a different visible city name.
        let next = { ...data, locationName: query }
        if (data.latitude && query.trim() !== data.locationName.trim()) {
            next = invalidateAutomaticTimezone({
                ...next,
                latitude: '',
                longitude: '',
            })
            // The zone is picked-place state exactly like the coordinates are. Left
            // behind, it would resolve the previous city's offset under the newly
            // typed name — the same class of mismatch the lat/lon clearing prevents.
        }
        onChange(next)

        setNoCityResults(false)
        setSearchError(null)
        setCityItems([])
        setShowCityDropdown(false)

        const trimmed = query.trim()
        if (trimmed.length < MIN_CITY_QUERY_LEN) {
            return
        }

        setLoadingCity(true)
        debounceRef.current = setTimeout(async () => {
            if (requestId !== searchSeqRef.current) return
            const controller = new AbortController()
            searchAbortRef.current = controller
            try {
                const { results } = await searchLocations(trimmed, {
                    // Lets the backend request names in the user's script, which
                    // is what makes a Telugu or Hindi query resolve at all.
                    language: i18n.resolvedLanguage || i18n.language,
                    limit: 8,
                    signal: controller.signal,
                })
                if (requestId !== searchSeqRef.current) return
                const items = results.map((d) => ({
                    id: String(d.place_id),
                    text: d.display_name,
                    // Strings, straight through — never re-derived from a
                    // coordinate array, so lat/lon cannot be transposed.
                    lat: d.lat,
                    lon: d.lon,
                    country:
                        d.address?.country ||
                        d.display_name.split(',').map((p) => p.trim()).pop() ||
                        '',
                    countryCode: String(d.address?.country_code || '').toUpperCase(),
                    // IANA zone, straight from the provider. Carried so the owning
                    // form can resolve the birth-instant offset without a second
                    // network round trip or a longitude guess.
                    timezoneName: d.timezone?.name || '',
                }))
                setCityItems(items)
                setShowCityDropdown(items.length > 0)
                setSearchError(null)
                setNoCityResults(items.length === 0)
            } catch (e: any) {
                // Aborted = the debouncer did its job. Leave state untouched,
                // otherwise every keystroke would flash an error.
                if (
                    requestId !== searchSeqRef.current ||
                    e?.code === 'ERR_CANCELED' ||
                    e?.name === 'CanceledError' ||
                    e?.name === 'AbortError'
                ) {
                    return
                }
                setCityItems([])
                setShowCityDropdown(false)
                setNoCityResults(false)
                setSearchError(e instanceof LocationSearchError ? e.kind : 'unknown')
            } finally {
                if (requestId === searchSeqRef.current) {
                    if (searchAbortRef.current === controller) searchAbortRef.current = null
                    setLoadingCity(false)
                }
            }
        }, 400)
    }

    const selectCity = async (item: any) => {
        cancelPendingCitySearch()
        resolvingRef.current = false
        setAutoResolving(false)
        setSearchError(null)
        lastResolvedQueryRef.current = item.text
        setCityQuery(item.text)
        setShowCityDropdown(false)
        setNoCityResults(false)
        
        let newData = invalidateAutomaticTimezone({
            ...data,
            locationName: item.text,
            latitude: parseFloat(item.lat).toFixed(6),
            longitude: parseFloat(item.lon).toFixed(6),
        }, item.timezoneName || '')

        if (autoFill) {
            // Country
            if (item.country) {
                const countryCode = String(item.countryCode || '').trim().toUpperCase()
                const match = findCountryMatch(item.country, countryCode)
                if (match) {
                    newData.country = match.id
                    newData.countryName = match.englishName
                } else {
                    if (countryCode) newData.country = countryCode
                    newData.countryName = item.country
                }
            }
        }
        
        // The IANA zone arrives with the selection itself, so there is nothing left
        // to fetch here — no second request, no 3-second timeout, no loading state.
        // Deriving the offset needs the birth date and time, which this component
        // does not own; the parent form does that via `resolveBirthTimezone`.
        latestDataRef.current = newData
        onChange(newData)
    }
    
    // Manual Country Change
    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const val = e.target.value
        const matchedCountry = countries.find(c => c.id === val)
        const newData = { ...data, country: val, countryName: matchedCountry?.englishName || '' }
        onChange(newData)
    }

    /**
     * Search button / Enter key.
     *
     * Runs the SAME backend autocomplete as the dropdown — the old separate
     * `limit=1` Nominatim lookup is gone, so there is no hidden second
     * geocoder dependency and no path where the two disagree.
     *
     * It auto-selects only when the top hit is an unambiguous locality:
     * exactly one candidate, or a clear exact name match. Otherwise it opens
     * the list and lets the user choose. Silently adopting a top hit for a
     * half-typed name is how a chart gets cast for the wrong city.
     */
    const manualSearch = async () => {
        if (!cityQuery) return
        if (resolvingRef.current) return
        const trimmed = cityQuery.trim()
        if (trimmed.length < MIN_CITY_QUERY_LEN) return
        if (data.latitude && data.longitude && lastResolvedQueryRef.current === trimmed) return
        cancelPendingCitySearch()
        const requestId = searchSeqRef.current
        const controller = new AbortController()
        searchAbortRef.current = controller
        resolvingRef.current = true
        setAutoResolving(true)
        setSearchError(null)
        try {
            const { results } = await searchLocations(trimmed, {
                language: i18n.resolvedLanguage || i18n.language,
                limit: 8,
                signal: controller.signal,
            })
            if (requestId !== searchSeqRef.current) return
            if (results.length === 0) {
                setNoCityResults(trimmed.length >= MIN_CITY_QUERY_LEN)
                setSearchError(null)
                toast({ title: t('chat.location_input.not_found'), variant: 'destructive' })
                return
            }

            const needle = trimmed.toLowerCase()
            const exact = results.filter(
                (r) => String(r.address?.city || '').trim().toLowerCase() === needle,
            )
            const unambiguous =
                results.length === 1 ? results[0] : exact.length === 1 ? exact[0] : null

            if (!unambiguous) {
                // Ambiguous — show the options rather than guessing for them.
                setCityItems(
                    results.map((d) => ({
                        id: String(d.place_id),
                        text: d.display_name,
                        lat: d.lat,
                        lon: d.lon,
                        country: d.address?.country || '',
                        countryCode: String(d.address?.country_code || '').toUpperCase(),
                        timezoneName: d.timezone?.name || '',
                    })),
                )
                setShowCityDropdown(true)
                setNoCityResults(false)
                setSearchError(null)
                return
            }

            setNoCityResults(false)
            setSearchError(null)
            await selectCity({
                id: String(unambiguous.place_id),
                text: unambiguous.display_name,
                lat: unambiguous.lat,
                lon: unambiguous.lon,
                country: unambiguous.address?.country || '',
                countryCode: String(unambiguous.address?.country_code || '').toUpperCase(),
                timezoneName: unambiguous.timezone?.name || '',
            })
            toast({ title: t('chat.location_input.location_updated') })
        } catch (e: any) {
            if (
                e?.code === 'ERR_CANCELED' ||
                e?.name === 'CanceledError' ||
                e?.name === 'AbortError'
            ) {
                return
            }
            setSearchError(e instanceof LocationSearchError ? e.kind : 'unknown')
            toast({ title: t('chat.location_input.error'), variant: 'destructive' })
        } finally {
            resolvingRef.current = false
            setAutoResolving(false)
        }
    }

    /**
     * Blur no longer geocodes.
     *
     * It used to fire `manualSearch()` on blur, which silently adopted the top
     * geocoder hit for whatever half-typed text happened to be in the field —
     * so tabbing away from "hyder" could commit Hyder, Alaska as a birthplace
     * without the user ever seeing it. Coordinates now change only on an
     * explicit pick or an explicit Search/Enter.
     *
     * Unresolved text is not silently accepted either: `latitude` stays empty
     * and the existing "pick from suggestions" guard below stays visible, so
     * the form still blocks at submit.
     */
    const handleCityBlur = () => {
        window.setTimeout(() => {
            if (resolvingRef.current) return
            setShowCityDropdown(false)
        }, 150)
    }

    return (
        <TooltipProvider delayDuration={0}>
        <div className="space-y-4">
             {/* City Search */}
            <div className="space-y-2" ref={wrapperRef}>
                <div className="flex justify-between">
                        <div className="flex items-center gap-2">
                            <Label>{label || t('chat.location_input.location')}</Label>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button
                                        type="button"
                                        className="inline-flex items-center justify-center rounded-full text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        aria-label={t('chat.location_input.location_help_aria')}
                                    >
                                        <Info className="h-4 w-4" />
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" align="start" className="max-w-[320px] text-xs leading-relaxed">
                                    {locationHelpText}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                     <div className="flex items-center space-x-2">
                        <Checkbox id="autofill" checked={autoFill} onCheckedChange={(c) => setAutoFill(!!c)} />
                        <label htmlFor="autofill" className="text-xs text-gray-500 cursor-pointer">{t('chat.location_input.autofill_details')}</label>
                     </div>
                </div>
                
                <div className="flex space-x-2 relative">
                    <div className="flex-1 flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                        <MapPin className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
                        <input
                            value={cityQuery}
                            onChange={(e) => handleCitySearch(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault()
                                    void manualSearch()
                                }
                            }}
                            onBlur={handleCityBlur}
                            placeholder={t('chat.location_input.type_city_name')}
                            // Browser autofill fills the text without picking a
                            // suggestion, so lat/lon/timezone stay unset and
                            // Generate fails. Non-address name + off keeps the
                            // address-autofill heuristics away.
                            name="city-search-q"
                            autoComplete="off"
                            spellCheck={false}
                            aria-autocomplete="list"
                            className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                        />
                        {(loadingCity || autoResolving) && <Loader2 className="mr-3 h-4 w-4 animate-spin text-gray-400 shrink-0" />}
                    </div>
                    <Button type="button" variant="outline" onClick={manualSearch}><Search className="h-4 w-4" /></Button>

                    {/* Dropdown.
                      * The scroll now lives on the ROW LIST, not the whole
                      * panel, so the attribution footer stays pinned and
                      * visible while the results scroll — which is what the
                      * provider's terms require. */}
                    {showCityDropdown && cityItems.length > 0 && (
                        <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden">
                            <div className="max-h-48 overflow-y-auto">
                                {cityItems.map((item) => (
                                    <div
                                        key={item.id}
                                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                        // mousedown, not click: the input's blur
                                        // handler closes the panel, and on some
                                        // browsers blur lands first and the click
                                        // never reaches the row.
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            selectCity(item)
                                        }}
                                    >
                                        {item.text}
                                    </div>
                                ))}
                            </div>
                            {/* URLs are fixed in the client, never taken from the
                              * response. The prose is translated; "Geoapify" and
                              * "OpenStreetMap" are proper names, so `translate="no"`
                              * also stops browser page-translation rewriting them. */}
                            <div
                                className="border-t border-gray-200 bg-gray-50 px-4 py-1.5 text-[11px] text-gray-500"
                                translate="no"
                            >
                                {t('chat.location_input.attribution_prefix')}{' '}
                                <a
                                    href={GEOAPIFY_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline"
                                >
                                    Geoapify
                                </a>
                                {' · '}
                                <a
                                    href={OSM_COPYRIGHT_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline"
                                >
                                    {t('chat.location_input.attribution_osm')}
                                </a>
                            </div>
                        </div>
                    )}
                </div>
                {autoResolving && !loadingCity && (
                    <div className="text-xs text-gray-500 mt-1">
                        {t('chat.location_input.resolving_location')}
                    </div>
                )}
                {/* Provider failure — distinct from "not found", and the only
                  * one worth a Retry. The typed query stays in the field so a
                  * retry costs one click, not retyping the city. */}
                {searchError && !loadingCity && !autoResolving && !showCityDropdown && (
                    <div className="text-xs text-red-600 mt-1 flex items-center gap-2">
                        <span>
                            {searchError === 'rate_limited'
                                ? t('chat.location_input.search_rate_limited')
                                : searchError === 'timeout'
                                  ? t('chat.location_input.search_timeout')
                                  : t('chat.location_input.search_unavailable')}
                        </span>
                        <button
                            type="button"
                            className="underline font-medium"
                            onClick={() => void manualSearch()}
                        >
                            {t('chat.location_input.retry')}
                        </button>
                    </div>
                )}
                {/* Typed (or auto-filled) text without a picked suggestion →
                  * no coordinates. Must be visible on phones — that's where
                  * browser autofill causes exactly this state. Suppressed while
                  * a provider error is showing so the two never stack. */}
                {cityQuery.trim().length > 0 && !data.latitude && !loadingCity && !autoResolving && !showCityDropdown && !searchError && (
                    <div className="text-xs text-amber-600 mt-1">
                        {t('chat.location_input.pick_from_list')}
                    </div>
                )}
                {(showInlineHelp || noCityResults) && !loadingCity && !autoResolving && (
                    <div className="text-xs text-gray-500 mt-1">
                        {locationHelpText}
                    </div>
                )}
            </div>

            {/* Country & Timezone Row */}
            <div className="grid grid-cols-[2fr_1.35fr] gap-4">
                <div className="space-y-2">
                    <Label className="flex items-center h-5">{t('chat.location_input.country')}</Label>
                    <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                        <Globe className="ml-3 h-4 w-4 text-gray-400 shrink-0" />
                        <select 
                            className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                            value={data.country || ''}
                            onChange={handleCountryChange}
                        >
                            <option value="">{t('chat.location_input.select_country')}</option>
                            {countries.map(c => (
                                <option key={c.id} value={c.id}>{c.text}</option>
                            ))}
                        </select>
                    </div>
                </div>

                 <div className="space-y-2 min-w-[130px]">
                    {timezoneSlot ?? (
                        <>
                            <Label className="flex items-center gap-2 h-5">
                                {t('chat.location_input.timezone_utc')}
                            </Label>
                            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                                <select
                                    className="flex h-10 w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                                    value={data.timezoneOffset}
                                    onChange={(e) => onChange({ ...data, timezoneOffset: e.target.value })}
                                >
                                    {tzOptions.map(opt => (
                                        <option key={opt.label} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Lat/Lon Row */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>{t('chat.location_input.latitude')}</Label>
                    <Input 
                        type="number" 
                        step="any" 
                        placeholder={t('chat.location_input.coords_autofill_placeholder', { defaultValue: 'Auto-filled' })}
                        value={data.latitude} 
                        onChange={(e) => onChange(invalidateAutomaticTimezone({
                            ...data,
                            latitude: e.target.value,
                        }))}
                    />
                </div>
                <div className="space-y-2">
                    <Label>{t('chat.location_input.longitude')}</Label>
                    <Input 
                        type="number" 
                        step="any" 
                        placeholder={t('chat.location_input.coords_autofill_placeholder', { defaultValue: 'Auto-filled' })}
                        value={data.longitude} 
                        onChange={(e) => onChange(invalidateAutomaticTimezone({
                            ...data,
                            longitude: e.target.value,
                        }))}
                    />
                </div>
            </div>
            <div className="text-xs text-gray-500">
                {t('chat.location_input.coords_autofill_helper', { defaultValue: 'Auto-filled after selecting a city.' })}
            </div>
        </div>
        </TooltipProvider>
    )
}
